
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronUp, ChevronDown } from 'lucide-react';

const ITEM_HEIGHT = 40;

interface ScrollColumnProps {
    items: (number | string)[];
    selected: number | string;
    onSelect: (item: any) => void;
    formatItem?: (item: any) => string;
    type: string;
}

const ScrollColumn: React.FC<ScrollColumnProps> = ({ items, selected, onSelect, formatItem }) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    // Initial scroll position
    useEffect(() => {
        if (scrollRef.current) {
            const index = items.indexOf(selected as never);
            if (index !== -1) {
                scrollRef.current.scrollTop = index * ITEM_HEIGHT;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); 

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const scrollTop = e.currentTarget.scrollTop;
        const index = Math.round(scrollTop / ITEM_HEIGHT);
        
        if (index >= 0 && index < items.length) {
            const newItem = items[index];
            if (newItem !== undefined && newItem !== selected) {
                onSelect(newItem);
            }
        }
    };

    const handleArrowClick = (direction: 'up' | 'down') => {
        if (scrollRef.current) {
            const currentScroll = scrollRef.current.scrollTop;
            const targetScroll = direction === 'up' 
                ? currentScroll - ITEM_HEIGHT 
                : currentScroll + ITEM_HEIGHT;
            
            scrollRef.current.scrollTo({
                top: targetScroll,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className="relative h-[120px] w-16 group flex flex-col items-center">
            {/* Arrows */}
            <button 
                className="absolute -top-6 left-0 right-0 flex justify-center text-[#D1D5DB] hover:text-[#5D1828] transition-colors z-20 p-1"
                onClick={(e) => { e.preventDefault(); handleArrowClick('up'); }}
            >
                <ChevronUp size={14} />
            </button>
            
            <div 
                ref={scrollRef}
                onScroll={handleScroll}
                className="h-full w-full overflow-y-auto snap-y snap-mandatory scrollbar-hide no-scrollbar"
                style={{ scrollBehavior: 'auto' }} 
            >
                {/* Top Padding */}
                <div style={{ height: ITEM_HEIGHT }} className="w-full flex-shrink-0"></div> 
                
                {items.map((item) => {
                    const isSelected = item === selected;
                    return (
                        <div 
                            key={item}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if(scrollRef.current) {
                                    scrollRef.current.scrollTo({
                                        top: items.indexOf(item as never) * ITEM_HEIGHT,
                                        behavior: 'smooth'
                                    });
                                }
                            }}
                            className={`h-[40px] flex items-center justify-center snap-center transition-all duration-200 cursor-pointer select-none w-full ${
                                isSelected 
                                    ? 'text-[#111827] font-bold text-lg scale-110' 
                                    : 'text-[#6B7280] font-medium text-sm opacity-50 hover:opacity-100 scale-90'
                            }`}
                        >
                            {formatItem ? formatItem(item) : item}
                        </div>
                    );
                })}
                
                {/* Bottom Padding */}
                <div style={{ height: ITEM_HEIGHT }} className="w-full flex-shrink-0"></div>
            </div>

            <button 
                className="absolute -bottom-6 left-0 right-0 flex justify-center text-[#D1D5DB] hover:text-[#5D1828] transition-colors z-20 p-1"
                onClick={(e) => { e.preventDefault(); handleArrowClick('down'); }}
            >
                <ChevronDown size={14} />
            </button>
        </div>
    );
};

interface CustomTimePickerProps {
  selectedTime: string;
  onChange: (time: string) => void;
  onClose: () => void;
}

export const CustomTimePicker: React.FC<CustomTimePickerProps> = ({ selectedTime, onChange, onClose }) => {
  // Parse initial time or default to 12:00 PM
  let initialDate;
  if (selectedTime) {
      initialDate = new Date(`2000-01-01T${selectedTime}`);
  } else {
      initialDate = new Date();
      initialDate.setHours(12, 0, 0, 0); // Default to 12:00 PM if empty
  }

  let initialHour = initialDate.getHours();
  const initialMinute = initialDate.getMinutes();
  const initialPeriod = initialHour >= 12 ? 'PM' : 'AM';
  
  if (initialHour === 0) initialHour = 12;
  else if (initialHour > 12) initialHour -= 12;

  // Safeguard against NaN if parsing failed
  if (isNaN(initialHour)) initialHour = 12;

  const [hour, setHour] = useState(initialHour);
  const [minute, setMinute] = useState(isNaN(initialMinute) ? 0 : initialMinute);
  const [period, setPeriod] = useState(initialPeriod);

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);
  const periods = ['AM', 'PM'];

  const handleSave = () => {
    let hour24 = hour;
    if (period === 'PM' && hour !== 12) hour24 += 12;
    if (period === 'AM' && hour === 12) hour24 = 0;
    
    const timeString = `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    onChange(timeString);
    onClose();
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 10, scale: 0.95 }}
      className="absolute top-full left-0 mt-2 bg-white rounded-2xl shadow-xl border border-[#D1D5DB] w-[280px] z-50 overflow-hidden flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="p-3 text-center border-b border-[#E5E7EB]">
        <span className="text-xs font-bold text-[#6B7280] uppercase tracking-wider">Select Time</span>
      </div>

      <div className="flex justify-center items-center py-8 relative bg-white">
        {/* Selection Highlight Bar (The "Lens") */}
        <div className="absolute left-4 right-4 h-[40px] bg-[#F9FAFB] border-y border-[#D1D5DB] rounded-lg pointer-events-none z-0"></div>
        
        {/* Columns Container */}
        <div className="flex items-center gap-1 z-10 h-[120px]">
            <ScrollColumn 
                items={hours} 
                selected={hour} 
                onSelect={setHour} 
                formatItem={(h) => h.toString().padStart(2, '0')}
                type="hour"
            />
            <div className="text-[#D1D5DB] font-bold pb-1 text-lg">:</div>
            <ScrollColumn 
                items={minutes} 
                selected={minute} 
                onSelect={setMinute} 
                formatItem={(m) => m.toString().padStart(2, '0')}
                type="minute"
            />
            <div className="w-2"></div>
            <ScrollColumn 
                items={periods} 
                selected={period} 
                onSelect={setPeriod} 
                type="period"
            />
        </div>
      </div>

      <div className="p-3 border-t border-[#E5E7EB] flex justify-between items-center bg-white z-20">
        <button 
          onClick={(e) => { e.preventDefault(); onClose(); }}
          className="px-4 py-1.5 text-sm font-medium text-[#6B7280] hover:text-[#111827] hover:bg-[#F3F4F6] rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button 
          onClick={(e) => { e.preventDefault(); handleSave(); }}
          className="px-6 py-1.5 text-sm font-bold text-white bg-[#5D1828] hover:bg-[#7A2235] rounded-lg shadow-md shadow-[#9E2A2F]/50 transition-all"
        >
          Save
        </button>
      </div>
    </motion.div>
  );
};